import React, { useState, useMemo } from 'react';
import {
  Paper,
  Text,
  Group,
  Badge,
  Stack,
  SimpleGrid,
  Box,
  Button,
  Center,
  ActionIcon,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';

interface CalendarViewProps {
  database: Database;
  items: DatabaseItem[];
  datePropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
}

/**
 * Calendar View Component (Stub)
 * Basic month calendar display
 * Future: Expand with drag-drop, event creation, time slots
 */
export const CalendarView: React.FC<CalendarViewProps> = ({
  database,
  items,
  datePropertyId,
  onItemSelect,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const itemsByDate = useMemo(() => {
    const map = new Map<string, DatabaseItem[]>();

    items.forEach(item => {
      if (!datePropertyId) return;
      const prop = item.properties.find(p => p.propertyId === datePropertyId);
      if (!prop?.value) return;

      const dateStr = String(prop.value);
      const key = dateStr.split('T')[0]; // Get date part only

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(item);
    });

    return map;
  }, [items, datePropertyId]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Days of month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="calendar-view-wrapper" style={{ width: '100%', padding: '20px' }}>
      <Paper p="md" radius="md" withBorder>
        {/* Calendar header */}
        <Group justify="space-between" align="center" mb="lg">
          <ActionIcon onClick={handlePrevMonth} variant="subtle">
            <IconChevronLeft size={16} />
          </ActionIcon>

          <Group gap="sm">
            <Text fw={600} size="lg">
              {monthName}
            </Text>
            <Button size="xs" variant="light" onClick={handleToday}>
              Today
            </Button>
          </Group>

          <ActionIcon onClick={handleNextMonth} variant="subtle">
            <IconChevronRight size={16} />
          </ActionIcon>
        </Group>

        {/* Day headers */}
        <SimpleGrid cols={7} mb="sm">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Center key={day} py="sm">
              <Text size="sm" fw={500}>
                {day}
              </Text>
            </Center>
          ))}
        </SimpleGrid>

        {/* Calendar grid */}
        <SimpleGrid cols={7} spacing="sm">
          {days.map((day, idx) => {
            if (day === null) {
              return <Box key={`empty-${idx}`} />;
            }

            const dateStr = `${currentDate.getFullYear()}-${String(
              currentDate.getMonth() + 1
            ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            const dayItems = itemsByDate.get(dateStr) || [];
            const isToday =
              day === new Date().getDate() &&
              currentDate.getMonth() === new Date().getMonth() &&
              currentDate.getFullYear() === new Date().getFullYear();

            return (
              <Paper
                key={day}
                p="xs"
                radius="sm"
                style={{
                  minHeight: '100px',
                  backgroundColor: isToday ? '#e7f5ff' : '#f8f9fa',
                  border: isToday ? '2px solid #1971c2' : '1px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'auto',
                }}
              >
                <Stack gap="xs">
                  <Text size="sm" fw={600}>
                    {day}
                  </Text>

                  {dayItems.length > 0 && (
                    <Stack gap="xs" style={{ maxHeight: '70px', overflowY: 'auto' }}>
                      {dayItems.slice(0, 2).map(item => (
                        <Paper
                          key={item.id}
                          p="4px"
                          radius="sm"
                          style={{
                            backgroundColor: '#fff',
                            borderLeft: '3px solid #1971c2',
                            cursor: 'pointer',
                          }}
                          onClick={() => onItemSelect?.(item)}
                        >
                          <Text size="xs" lineClamp={1}>
                            {item.title}
                          </Text>
                        </Paper>
                      ))}
                      {dayItems.length > 2 && (
                        <Text size="xs" c="dimmed">
                          +{dayItems.length - 2} more
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </SimpleGrid>

        {/* Footer note */}
        <Group justify="center" mt="lg">
          <Text size="xs" c="dimmed">
            Calendar view - Future: Drag items to reschedule, click to create events
          </Text>
        </Group>
      </Paper>
    </div>
  );
};

export default CalendarView;
